import React, { useRef, useEffect, useState } from "react";
import DottedMap from "dotted-map";
import radioData from "./radioData.json";
import { getArtistIdsForCity } from "./cityArtistsData";
import { ARTISTS_DATA } from "./artistData";
import ArtistCard from "./ArtistCard";

// Interface for city coordinates
interface CityData {
  key: string;
  lat: number;
  lng: number;
  name: string;
}

// Create a lookup table for city coordinates from JSON data
const cities: Record<string, CityData> = {};
const cityNameToKey: Record<string, string> = {};

radioData.cities.forEach(city => {
  cities[city.key] = {
    key: city.key,
    lat: city.lat,
    lng: city.lng,
    name: city.name
  };
  // Create mapping from display name to city key
  cityNameToKey[city.name] = city.key;
});

// Function to add a pin by city name
function addPinByCity(map: any, city: keyof typeof cities, data: string) {
  if (cities[city]) {
    const { lat, lng } = cities[city];
    map.addPin({ lat, lng, data });
  } else {
    console.error(`Coordinates for ${city} not found.`);
  }
}

// Initialize the dotted map
const map = new DottedMap({ height: 100, grid: "vertical" });

// Add pins for the specified cities
Object.keys(cities).forEach((city) => {
  addPinByCity(map, city as keyof typeof cities, cities[city as keyof typeof cities].name);
});

// Get points and filter out those with data
const allPoints = map.getPoints();
// Get all city pins first
const allPins = allPoints.filter((point) => point.data);

// Filter to create a proper grid with even spacing (every 3 units)
const gridPoints = allPoints.filter((point) => {
  const spacing = 3; // Adjust this value to control spacing
  return Math.round(point.x) % spacing === 0 && Math.round(point.y) % spacing === 0;
});

// Function to snap a point to the nearest grid position
function snapToGrid(point: any, spacing: number) {
  const snappedX = Math.round(point.x / spacing) * spacing;
  const snappedY = Math.round(point.y / spacing) * spacing;
  return { ...point, x: snappedX, y: snappedY };
}

// Function to tilt grid points to the left by modifying their coordinates
function tiltGridPoints(points: any[], tiltAngle: number = -0.2) {
  return points.map(point => {
    // Apply a slight horizontal offset based on Y position to create tilt
    // The further down (higher Y), the more the point shifts left
    const tiltedX = point.x + (point.y * tiltAngle);
    return { ...point, x: tiltedX };
  });
}

// Snap city pins to the nearest grid points
const snappedPins = allPins.map(pin => snapToGrid(pin, 3));
console.log('Snapped pins:', snappedPins.length);

// Remove duplicate snapped pins (multiple cities might snap to same position)
const uniqueSnappedPins = snappedPins.filter((pin, index, array) => 
  array.findIndex(p => p.x === pin.x && p.y === pin.y) === index
);
console.log('Unique snapped pins:', uniqueSnappedPins.length);

// Create a map of grid points to avoid duplicates
const gridPointMap: Record<string, any> = {};
gridPoints.forEach(point => {
  const key = `${point.x},${point.y}`;
  gridPointMap[key] = point;
});

// Add snapped city pins, replacing any grid points at the same position
uniqueSnappedPins.forEach(pin => {
  const key = `${pin.x},${pin.y}`;
  gridPointMap[key] = pin; // This will replace any existing grid point with the city pin
});

// Convert back to array
const points = Object.values(gridPointMap);

// Apply tilt transformation to all points
const tiltedPoints = tiltGridPoints(points, -0.35); // Adjust tilt angle here (-0.15 for left tilt)

const pins = tiltedPoints.filter((point) => point.data);
console.log('Final pins count:', pins.length);

const getSvgOptions = (theme: string) => {
  if (theme === "blue") {
    return {
      backgroundColor: "#3D71A3",
      color: "#FFFFFF", // White grid dots
      radius: 1,
      pinColor: "#FFFFFF", // White pins
      pinRadius: 2,
      textColor: "#FFFFFF",
      textBackgroundColor: "rgba(255, 255, 255, 0.2)",
      textPadding: 2,
      textFontSize: 3,
      textRadius: 2,
    };
  }
  
  // Default theme
  return {
    backgroundColor: "#FFFFFF",
    color: "#3D71A3", // Color for grid dots
    radius: 1, // Radius for grid dots
    pinColor: "#1B1B1B", // Color for country pins
    pinRadius: 2, // Slightly larger radius for country pins
    textColor: "#000000", // Text color for country names
    textBackgroundColor: "#f0f0f0", // Background color for country name boxes
    textPadding: 2, // Padding for the text box
    textFontSize: 3, // Font size for the text
    textRadius: 2, // Border radius for the text box
  };
};

interface MapProps {
  theme?: string;
  showTooltips?: boolean;
}

const Map: React.FC<MapProps> = ({
  theme = "default",
  showTooltips = true
}) => {
  const svgOptions = getSvgOptions(theme);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });
  const [modalCity, setModalCity] = useState<string | null>(null);
  const [modalCityData, setModalCityData] = useState<CityData | null>(null);
  const [modalArtists, setModalArtists] = useState<any[]>([]);
  const [modalPosition, setModalPosition] = useState<{ x: number; y: number } | null>(null);
  const [rawClickPosition, setRawClickPosition] = useState<{ x: number; y: number } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalTransform, setModalTransform] = useState<string>('translate(-50%, -100%)');

  // After the popover renders, clamp its position within container margins based on real size
  useEffect(() => {
    if (!rawClickPosition || !containerRef.current || !modalRef.current) return;
    const bounds = containerRef.current.getBoundingClientRect();
    const width = bounds.width;
    const height = bounds.height;
    const margin = 40;
    const modalWidth = modalRef.current.offsetWidth;
    const modalHeight = modalRef.current.offsetHeight;

    // Clamp so the popover stays within margins horizontally
    const minCenterX = margin + modalWidth / 2;
    const maxCenterX = width - margin - modalWidth / 2;
    const clampedCenterX = Math.max(minCenterX, Math.min(maxCenterX, rawClickPosition.x));

    // Clamp Y so the anchor point isn't too close to top/bottom; popover opens upward
    const clampedY = Math.max(margin + 8, Math.min(height - margin - 8, rawClickPosition.y));

    setModalPosition({ x: clampedCenterX, y: clampedY });

    // Maintain centered transform; no edge-based shift needed when center is clamped
    setModalTransform('translate(-50%, -100%)');
  }, [rawClickPosition]);

  // Calculate the actual bounds of all points to determine proper viewBox
  const calculateContentBounds = () => {
    if (tiltedPoints.length === 0) return { minX: 0, minY: 0, maxX: 200, maxY: 200 };
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    tiltedPoints.forEach(point => {
      const radius = point.data ? svgOptions.pinRadius : svgOptions.radius;
      minX = Math.min(minX, point.x - radius);
      minY = Math.min(minY, point.y - radius);
      maxX = Math.max(maxX, point.x + radius);
      maxY = Math.max(maxY, point.y + radius);
    });
    
    // Add some padding around the content
    const padding = 10;
    return {
      minX: Math.max(0, minX - padding),
      minY: Math.max(0, minY - padding),
      maxX: maxX + padding,
      maxY: maxY + padding
    };
  };

  const contentBounds = calculateContentBounds();
  const viewBox = `${contentBounds.minX} ${contentBounds.minY} ${contentBounds.maxX - contentBounds.minX} ${contentBounds.maxY - contentBounds.minY}`;
  
  // Get actual SVG dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setSvgDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Function to convert SVG coordinates to screen coordinates
  const getScreenCoordinates = (svgX: number, svgY: number) => {
    if (svgDimensions.width === 0 || svgDimensions.height === 0) {
      return { x: 0, y: 0 };
    }
    
    // Calculate the actual viewBox dimensions
    const viewBoxWidth = contentBounds.maxX - contentBounds.minX;
    const viewBoxHeight = contentBounds.maxY - contentBounds.minY;
    
    // Scale coordinates relative to the actual viewBox
    const scaleX = svgDimensions.width / viewBoxWidth;
    const scaleY = svgDimensions.height / viewBoxHeight;
    
    // Adjust coordinates relative to the viewBox origin
    const adjustedX = svgX - contentBounds.minX;
    const adjustedY = svgY - contentBounds.minY;
    
    return {
      x: adjustedX * scaleX,
      y: adjustedY * scaleY
    };
  };

  return (
    <>
      {/* Render the SVG map with gradient overlays */}
      <div ref={containerRef} className="svg-container" style={{ position: 'relative' }}>
        <svg 
          ref={svgRef}
          viewBox={viewBox} 
          preserveAspectRatio="none" 
          style={{ background: svgOptions.backgroundColor }}
        >
        {/* Define clipPath for circular images */}
        <defs>
          <clipPath id="imageClip">
            <circle cx="4" cy="4" r="4" />
          </clipPath>
        </defs>
        
        {tiltedPoints.map((point, index) => {
          const isPin = pins.some((p) => p.x === point.x && p.y === point.y);
          
          return (
            <g key={index}>
              {isPin ? (
                <g>
                  {/* Pin Icon */}
                  <g
                    transform={`translate(${point.x}, ${point.y}) scale(0.15) translate(-8.5, -6.5)`}
                    style={{ 
                      opacity: 1
                    }}
                  >
                    <path d="M10.9897 0.966752C9.80468 1.02062 8.99847 1.91301 8.42389 2.67253C8.18329 2.99214 7.96602 3.31355 7.78467 3.63675C7.63025 3.91326 7.45968 4.21312 7.34297 4.48784C7.31065 4.56325 7.29269 4.65303 7.33399 4.70869C7.3681 4.75717 7.40761 4.76077 7.492 4.75717C7.71644 4.7464 7.99475 4.69612 8.22997 4.67278C8.95358 4.60276 9.61435 4.63867 10.2931 4.66021C10.4942 4.6674 10.7186 4.71588 10.9377 4.56325C11.1226 4.43397 11.1873 4.20594 11.2555 4.05332C11.5715 3.35305 11.9755 2.52889 12.0653 1.85914C12.1407 1.29714 11.7744 0.93084 10.9897 0.966752ZM5.72337 12.0759H5.19009C5.1452 12.0633 5.08236 12.0723 5.02849 12.0705C3.38017 12.013 2.04248 11.6431 1.13393 10.8226C-0.122963 9.68957 -0.363568 8.0592 0.555758 6.19721C0.561144 6.18644 0.573713 6.17028 0.564735 6.16489C0.595259 6.10564 0.622193 6.04818 0.654513 5.98893C1.39069 4.61712 2.48239 3.38178 4.12892 2.31522C6.07889 1.05114 8.38618 0.180298 10.5409 0.0348577C11.5535 -0.0333738 12.6794 -0.0100307 13.5358 0.182094C15.3673 0.591481 16.8181 1.7981 16.626 3.43564C16.5739 3.88094 16.4231 4.50759 16.1466 4.97085C15.818 5.52388 15.1734 5.76089 14.5072 5.83092C12.7997 6.01406 11.3435 5.73037 9.76517 5.66932C8.99308 5.63879 8.08633 5.62982 7.24421 5.77526C6.49905 5.90274 6.08608 6.09666 5.7521 6.68022C4.9459 8.09332 6.09146 9.02162 7.68592 9.11679C8.81532 9.18502 10.2338 8.92466 11.4692 8.43986C12.2287 8.1418 12.9631 7.70548 13.6472 7.28173C14.0368 7.03933 14.4749 6.67124 14.9148 6.74665C15.3152 6.81488 15.0513 7.23504 14.8789 7.50258C13.9147 9.00546 11.8319 10.4796 9.84059 11.2697C8.48674 11.8065 7.0072 12.0435 5.72337 12.0759Z" fill={svgOptions.pinColor}/>
                  </g>
                  {/* Click target over pin */}
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={3}
                    fill="transparent"
                    stroke="transparent"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const city = point.data as string;
                      setModalCity(city);
                      const cityKey = cityNameToKey[city];
                      setModalCityData(cities[cityKey]);
                      const artistIds = getArtistIdsForCity(cityKey);
                      const artists = artistIds
                        .map(id => ARTISTS_DATA.find(artist => artist.id === id))
                        .filter(Boolean) as any[];
                      setModalArtists(artists);
                      const pinCoords = getScreenCoordinates(point.x, point.y);
                      // Calculate city label position for popup
                      const cityName = point.data as string;
                      const isSpecialTopCity = ['Paris', 'Amsterdam', 'Seoul'].includes(cityName);
                      const isSpecialBottomCity = ['Lisbon', 'Bangkok'].includes(cityName);
                      const isTopLeftCity = ['Vancouver', 'Montreal', 'Paris', 'Taipei', 'Seoul', 'London'].includes(cityName);
                      const isTopRightCity = ['New York', 'Tokyo', 'Berlin', 'Amsterdam'].includes(cityName);
                      const isTopCity = isTopLeftCity || isTopRightCity;
                      const cityY = isSpecialTopCity ? 30 : (isSpecialBottomCity ? svgDimensions.height - 100 : (isTopCity ? 70 : svgDimensions.height - 60));
                      const coords = { x: pinCoords.x, y: cityY };
                      // Set initial position so the popover renders immediately
                      setModalPosition(coords);
                      setRawClickPosition(coords);
                    }}
                  />
                </g>
              ) : (
                <g
                  transform={`translate(${point.x}, ${point.y}) scale(0.15) translate(-5,7)`}
                  style={{ 
                    opacity: 1,
                    cursor: 'default'
                  }}
                >
                  <path d="M12.5101 5.1789C11.2389 7.67113 7.65852 9.69113 4.5127 9.69113C1.36509 9.69113 -0.15575 7.67113 1.11551 5.1789C2.38496 2.68666 5.9671 0.66666 9.11292 0.66666C12.2605 0.66666 13.7814 2.68666 12.5101 5.1789Z" stroke={svgOptions.color} strokeWidth="1.33333" strokeMiterlimit="10" fill="none"/>
                </g>
              )}
              
            </g>
          );
        })}
        </svg>
        
        {/* City names positioned outside SVG */}
        {showTooltips && pins.map((pin, index) => {
          const city = pin.data;
          const coords = getScreenCoordinates(pin.x, pin.y);
          
          // Cities in top row (left side)
          const topLeftCities = [
            'Vancouver', 'Montreal', 'Paris', 'Taipei', 'Seoul', 'London'
          ];
          
          // Cities in top row (right side)
          const topRightCities = [
            'New York', 'Tokyo', 'Berlin', 'Amsterdam'
          ];
          
          // Cities in bottom row (left side)
          const bottomLeftCities = [
            'Los Angeles', 'Lisbon', 'Barcelona', 'Bangkok', 'Sydney', 'Shanghai'
          ];
          
          // Cities in bottom row (right side)
          const bottomRightCities = [
            'Mexico City', 'Roma', 'Istanbul'
          ];
          
          const isTopLeftCity = topLeftCities.includes(city);
          const isTopRightCity = topRightCities.includes(city);
          const isTopCity = isTopLeftCity || isTopRightCity;
          const isBottomLeftCity = bottomLeftCities.includes(city);
          const isBottomRightCity = bottomRightCities.includes(city);
          const isBottomCity = isBottomLeftCity || isBottomRightCity;
          
          // Special positioning for Paris and Amsterdam
          const isSpecialTopCity = ['Paris', 'Amsterdam', 'Seoul'].includes(city);
          
          // Special positioning for bottom cities
          const isSpecialBottomCity = ['Lisbon', 'Bangkok'].includes(city);
          
          // Calculate dynamic line positioning based on distance from city name to pin
          const cityY = isSpecialTopCity ? 30 : (isSpecialBottomCity ? svgDimensions.height - 100 : (isTopCity ? 70 : svgDimensions.height - 60)); // City name Y position
          const pinY = coords.y; // Pin Y position
          
          // Calculate line start position (bottom of city name text)
          const textHeight = 19; // Approximate text height
          const lineStartY = cityY - (0.2 * textHeight); // Account for transform translate(-100%, -120%)
          
          // Calculate line height to reach the pin 
          const lineHeight = Math.abs(pinY - lineStartY);
          
          return (
             <div
               key={`city-${index}`}
               className="city-label"
               style={{
                 position: 'absolute',
                 left: `${coords.x}px`,
                 top: isSpecialTopCity ? '-40px' : (isSpecialBottomCity ? `${svgDimensions.height - 80}px` : (isTopCity ? '0px' : `${svgDimensions.height - 40}px`)), // Special positioning for Paris, Amsterdam, Seoul and Lisbon, Bangkok
                 transform: (isTopRightCity || isBottomRightCity) ? 'translate(0%, -120%)' : 'translate(-100%, -120%)', // Position to the right for right cities, left for left cities
                 color: svgOptions.textColor,
                 fontFamily: "'Adieu', sans-serif",
                 fontWeight: '400',
                 fontSize: '12px',
                 pointerEvents: 'none',
                 userSelect: 'none',
                 whiteSpace: 'nowrap',
                 zIndex: 20, // Higher than gradient overlays
                 
               }}
             >
             <div style={{ position: 'relative' }}>
               {/* Vertical line for bottom left cities */}
               {isBottomLeftCity && (
                 <div
                   style={{
                     position: 'absolute',
                     left: 'auto',
                     right: 0,
                     top: `-${lineHeight}px`,
                     width: '1px',
                     height: `${lineHeight}px`,
                     backgroundColor: svgOptions.textColor,
                     zIndex: 1,
                   }}
                 />
               )}
               
               {/* Vertical line for bottom right cities */}
               {isBottomRightCity && (
                 <div
                   style={{
                     position: 'absolute',
                     left: 0,
                     right: 'auto',
                     top: `-${lineHeight}px`,
                     width: '1px',
                     height: `${lineHeight}px`,
                     backgroundColor: svgOptions.textColor,
                     zIndex: 1,
                   }}
                 />
               )}
               
               {/* Vertical line for top left cities */}
               {isTopLeftCity && (
                 <div
                   style={{
                     position: 'absolute',
                     left: 'auto',
                     right: 0,
                     top: `${textHeight }px`,
                     width: '1px',
                     height: `${lineHeight + 70}px`,
                     backgroundColor: svgOptions.textColor,
                     zIndex: 1,
                   }}
                 />
               )}
               
               {/* Vertical line for top right cities */}
               {isTopRightCity && (
                 <div
                   style={{
                     position: 'absolute',
                     left: 0,
                     right: 'auto',
                     top: `${textHeight}px`,
                     width: '1px',
                     height: `${lineHeight + 70}px`,
                     backgroundColor: svgOptions.textColor,
                     zIndex: 1,
                   }}
                 />
               )}
               
              <p
               onClick={() => {
                 setModalCity(city);
                 
                 // Convert display name to city key for lookup
                 const cityKey = cityNameToKey[city];
                 setModalCityData(cities[cityKey]);
                 
                 // Get artist IDs for this city from cityArtistsData
                 const artistIds = getArtistIdsForCity(cityKey);
                 
                 // Resolve artist IDs to full artist data
                 const artists = artistIds.map(id => 
                   ARTISTS_DATA.find(artist => artist.id === id)
                 ).filter(Boolean);
                 
                 setModalArtists(artists);
                 // Position popover at the city's label coordinates
                 const cityY = isSpecialTopCity ? 30 : (isSpecialBottomCity ? svgDimensions.height - 100 : (isTopCity ? 70 : svgDimensions.height - 60));
                 const clickCoords = { x: coords.x, y: cityY };
                 setModalPosition(clickCoords);
                 setRawClickPosition(clickCoords);
               }}
               style={{
                textTransform: 'lowercase',
                 ...(isTopCity ? {
                   // Top cities get border-bottom
                   borderBottom: `1px solid ${svgOptions.textColor}`,
                   paddingBottom: '4px'
                 } : {
                   // Bottom cities get border-top
                   borderTop: `1px solid ${svgOptions.textColor}`,
                   paddingTop: '4px',
                   marginBottom: `-${textHeight}px`
                }),
                // Make only the text clickable while keeping the rest non-interactive
                pointerEvents: 'auto',
                cursor: 'pointer'
               }}
              >
                 {city}
               </p>
               
             </div>
            </div>
          );
        })}
        
        {/* Gradient overlays */}
        <div className="svg-gradient-top"></div>
        <div className="svg-gradient-bottom"></div>

        {/* Floating popover modal at click position (no overlay) */}
        {modalCity && modalCityData && modalPosition && (
          <div
            className="modal-content modal-popover"
            style={{
              position: 'absolute',
              left: `${modalPosition.x}px`,
              top: `${modalPosition.y}px`,
              zIndex: 1000,
              transform: modalTransform
            }}
            ref={modalRef}
            role="dialog"
            aria-modal="false"
          >
            <button
              className="modal-close"
              onClick={() => {
                setModalCity(null);
                setModalCityData(null);
                setModalArtists([]);
                setModalPosition(null);
              }}
              aria-label="Close"
            >
              ×
            </button>
            <div className="modal-body">
              <h2>{modalCity}</h2>
              <p className="modal-subtitle">Featured Artists</p>
              {modalArtists && modalArtists.length > 0 ? (
                <div className="modal-artists-list">
                  {modalArtists.map((artist) => (
                    <ArtistCard 
                      key={artist.id} 
                      artist={artist} 
                      variant="list" 
                    />
                  ))}
                </div>
              ) : (
                <p className="modal-no-artists">No artists available for this city.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Map;
